<template>
  <div class="card card-custom gutter-b">
    <div class="card-body">
      <div class="row">
        <div class="col-xl-6 mb-5 mb-lg-0 d-flex flex-column">
          <div class="d-flex justify-content-between align-items-center mb-5">
            <h5
              class="mb-2 font-weight-bolder font-size-md grey--text"
              v-if="
                profile.basicInfo.availability_updated_by ||
                profile.basicInfo.availability_updated_at
              "
            >
              Availability last Updated
              <template v-if="profile.basicInfo.availability_updated_by">
                by {{ profile.basicInfo.availability_updated_by.name }}
              </template>
              <template v-if="profile.basicInfo.availability_updated_at">
                on
                {{
                  new Date(
                    profile.basicInfo.availability_updated_at
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                }}
              </template>
            </h5>
            <h5 v-else>
              <span class="font-weight-bolder font-size-md grey--text">
                Availability & Salary
              </span>
            </h5>

            <div class="d-flex">
              <b-button
                v-if="!isEditing"
                size="sm"
                variant="outline-info"
                class="px-3 mr-2"
                @click="startEditing"
                ><i class="fas fa-pen px-0"></i
              ></b-button>
              <template v-else>
                <b-button
                  size="sm"
                  variant="info"
                  class="px-3 mr-2"
                  @click="handleSaveAll"
                  >OK</b-button
                >
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="px-3"
                  @click="cancelAllChanges"
                  >Cancel</b-button
                >
              </template>
            </div>
          </div>

          <h6 class="mb-1 font-weight-bolder font-size-sm">
            Full Time Availability
          </h6>
          <div class="d-flex">
            <select
              ref="fullTimeAvailability"
              :disabled="!isEditing"
              :read-only="!isEditing"
              v-model="fullTimeAvailability"
              class="custom-select"
            >
              <option :value="null" disabled>Please select an option</option>
              <option
                :value="option.id"
                v-for="option in AVAILABILITY_OPTIONS"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </select>
          </div>
          <VulidateError
            :validationField="$v.fullTimeAvailability"
            :params="['required']"
          />
          <div class="mt-4">
            <h6 class="mb-1 font-weight-bolder font-size-sm">
              Part Time Availability
            </h6>
            <div class="d-flex">
              <select
                ref="partTimeAvailability"
                :disabled="!isEditing"
                :read-only="!isEditing"
                v-model="partTimeAvailability"
                class="custom-select"
              >
                <option :value="null" disabled>Please select an option</option>
                <option
                  :value="option.id"
                  v-for="option in AVAILABILITY_OPTIONS"
                  :key="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
            </div>
            <VulidateError
              :validationField="$v.partTimeAvailability"
              :params="['required']"
            />
          </div>

          <div class="mt-4">
            <h5
              class="mb-2 font-weight-bolder font-size-md grey--text"
              v-if="
                profile.basicInfo.salary_updated_by ||
                profile.basicInfo.salary_updated_at
              "
            >
              Salary last Updated
              <template v-if="profile.basicInfo.salary_updated_by">
                by {{ profile.basicInfo.salary_updated_by.name }}
              </template>
              <template v-if="profile.basicInfo.salary_updated_at">
                on
                {{
                  new Date(
                    profile.basicInfo.salary_updated_at
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                }}
              </template>
            </h5>
            <h6 class="mb-1 font-weight-bolder font-size-sm">
              Expected Full Time Salary
            </h6>
            <div class="d-flex">
              <input
                class="form-control form__input"
                :disabled="!isEditing"
                :read-only="!isEditing"
                ref="fullTimeSalary"
                v-model.number="fullTimeSalary"
                placeholder="Enter Full Time Salary"
                :class="{
                  'form__input--error':
                    $v.fullTimeSalary.$dirty && $v.fullTimeSalary.$error,
                }"
              />
            </div>

            <VulidateError
              :validationField="$v.fullTimeSalary"
              :params="[
                'integer',
                {
                  minValue: { number: $v.fullTimeSalary.$params.minValue.min },
                },
                {
                  maxValue: { number: $v.fullTimeSalary.$params.maxValue.max },
                },
              ]"
            />
          </div>
          <div class="mt-4">
            <h6 class="mb-1 font-weight-bolder font-size-sm">
              Expected Part Time Salary
            </h6>
            <div class="d-flex">
              <input
                class="form-control form__input"
                :disabled="!isEditing"
                :read-only="!isEditing"
                v-model.number="partTimeSalary"
                ref="partTimeSalary"
                placeholder="Enter Part Time Salary"
                :class="{
                  'form__input--error':
                    $v.partTimeSalary.$dirty && $v.partTimeSalary.$error,
                }"
              />
            </div>

            <VulidateError
              :validationField="$v.partTimeSalary"
              :params="[
                'integer',
                {
                  minValue: { number: $v.partTimeSalary.$params.minValue.min },
                },
                {
                  maxValue: { number: $v.partTimeSalary.$params.maxValue.max },
                },
              ]"
            />
          </div>
        </div>

        <div class="col-xl-6 d-flex flex-column">
          <div class="shadow p-4 flex-grow-1">
            <div class="row">
              <div class="col-12 col-md-6">
                <p class="font-size-lg mb-3">
                  Margin =
                  <strong class="text-info">
                    ${{ profile.basicInfo.full_price.margin.margin }}
                  </strong>
                  <strong v-if="false">0</strong>
                </p>
                <p class="font-size-lg mb-3">
                  <i
                    class="flaticon2-information icon-md text-info mr-1"
                    v-b-tooltip="`WHT = 5% from (Full-time Expected Salary)`"
                  ></i>
                  WHT =
                  <strong class="text-info">
                    ${{ profile.basicInfo.full_price.margin.wht_value }}
                  </strong>
                </p>
                <p class="font-size-lg mb-3">
                  Full Time Expected Salary:
                  <strong class="text-info" v-if="profile.basicInfo.salary">
                    ${{ profile.basicInfo.salary }}</strong
                  >
                  <strong v-else> -</strong>
                </p>
                <p class="mb-0 font-size-lg">
                  Total Full Time Salary with Margin :
                  <strong
                    class="text-info"
                    v-if="profile.basicInfo.full_price.total"
                  >
                    ${{ profile.basicInfo.full_price.total }}</strong
                  >
                  <strong v-else>-</strong>
                </p>
              </div>
              <div class="col-12 col-md-6" v-if="isSaudiTalent">
                <p class="mb-0 font-size-lg mb-3">
                  GOSI:
                  <strong class="text-info">{{profile.basicInfo.full_price?.margin?.social_insurance ? `$${profile.basicInfo.full_price?.margin?.social_insurance}` : "N/A"}}</strong>
                </p>
                <p class="mb-0 font-size-lg mb-3">
                  Medical Insurance:
                  <strong class="text-info">{{profile.basicInfo.full_price?.margin?.medical_insurance ? `$${profile.basicInfo.full_price?.margin?.medical_insurance}` : "N/A"}}</strong>
                </p>
                <p class="mb-0 font-size-lg mb-3">
                  EOS:
                  <strong class="text-info">{{profile.basicInfo.full_price?.margin?.end_of_service ? `$${profile.basicInfo.full_price?.margin?.end_of_service}` : "N/A"}}</strong>
                </p>
                <p class="mb-0 font-size-lg mb-3">
                  Leave Balance:
                  <strong class="text-info">{{profile.basicInfo.full_price?.margin?.leave_balance ? `$${profile.basicInfo.full_price?.margin?.leave_balance}` : "N/A"}}</strong>
                </p>
              </div>
            </div>
          </div>

          <div class="shadow p-4 flex-grow-1 mt-4">
            <div class="row">
              <div class="col-12 col-md-6">
                <p class="mb-3 font-size-lg">
                  Margin =
                  <strong class="text-info">
                    ${{ profile.basicInfo.part_price.margin.margin }}
                  </strong>
                  <strong class="text-info" v-if="false">0</strong>
                </p>
                <p class="mb-3 font-size-lg">
                  <i
                    class="flaticon2-information icon-md text-info mr-1"
                    v-b-tooltip="`WHT = 5% from (Part-time Expected Salary)`"
                  ></i>
                  WHT =
                  <strong class="text-info">
                    ${{ profile.basicInfo.part_price.margin.wht_value }}
                  </strong>
                </p>
                <p class="mb-3 font-size-lg">
                  Part Time Expected Salary:
                  <strong
                    class="text-info"
                    v-if="profile.basicInfo.part_time_salary"
                  >
                    ${{ profile.basicInfo.part_time_salary }}
                  </strong>
                  <strong v-else>-</strong>
                </p>
                <p class="mb-0 font-size-lg">
                  Total Part Time Salary with Margin :
                  <strong
                    class="text-info"
                    v-if="profile.basicInfo.part_price.total"
                  >
                    ${{ profile.basicInfo.part_price.total }}
                  </strong>
                  <strong v-else> -</strong>
                </p>
              </div>
              <div class="col-12 col-md-6" v-if="isSaudiTalent">
                <p class="mb-0 font-size-lg mb-3">
                  GOSI:
                  <strong class="text-info">{{profile.basicInfo.part_price?.margin?.social_insurance ? `$${profile.basicInfo.part_price?.margin?.social_insurance}` : "N/A"}}</strong>
                </p>
                <p class="mb-0 font-size-lg mb-3">
                  Medical Insurance:
                  <strong class="text-info">{{profile.basicInfo.part_price?.margin?.medical_insurance ? `$${profile.basicInfo.part_price?.margin?.medical_insurance}` : "N/A"}}</strong>
                </p>
                <p class="mb-0 font-size-lg mb-3">
                  EOS:
                  <strong class="text-info">{{profile.basicInfo.part_price?.margin?.end_of_service ? `$${profile.basicInfo.part_price?.margin?.end_of_service}` : "N/A"}}</strong>
                </p>
                <p class="mb-0 font-size-lg mb-3">
                  Leave Balance:
                  <strong class="text-info">{{profile.basicInfo.part_price?.margin?.leave_balance ? `$${profile.basicInfo.part_price?.margin?.leave_balance}` : "N/A"}}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  required,
  integer,
  minValue,
  maxValue,
} from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import AVAILABILITY_OPTIONS from "@/constants/availability-options";
import { SALARY_MIN_VALUE, SALARY_MAX_VALUE } from "@/constants/salary";
import VulidateError from "@/components/shared/vulidateError.vue";
export default {
  name: "SalaryAndAvailability",
  mixins: [validationMixin],
  components: { VulidateError },
  props: {
    profile: {
      required: true,
      type: Object,
    },
    profileId: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      AVAILABILITY_OPTIONS,
      isEditing: false,
      fullTimeAvailability: null,
      partTimeAvailability: null,
      fullTimeSalary: null,
      partTimeSalary: null,
      originalValues: {
        fullTimeAvailability: null,
        partTimeAvailability: null,
        fullTimeSalary: null,
        partTimeSalary: null,
      },
    };
  },
  computed: {
    hasChanges() {
      return (
        this.fullTimeAvailability !==
          this.originalValues.fullTimeAvailability ||
        this.partTimeAvailability !==
          this.originalValues.partTimeAvailability ||
        this.fullTimeSalary !== this.originalValues.fullTimeSalary ||
        this.partTimeSalary !== this.originalValues.partTimeSalary
      );
    },
    isSaudiTalent() {
      return this.profile.basicInfo.nationality?.id === 187;
    },
  },
  watch: {
    profile: {
      immediate: true,
      handler(profile) {
        this.initSalaryData(profile);
      },
    },
  },
  validations: {
    partTimeSalary: {
      integer,
      minValue: minValue(SALARY_MIN_VALUE),
      maxValue: maxValue(SALARY_MAX_VALUE),
    },
    fullTimeSalary: {
      integer,
      minValue: minValue(SALARY_MIN_VALUE),
      maxValue: maxValue(SALARY_MAX_VALUE),
    },
    partTimeAvailability: {
      required,
    },
    fullTimeAvailability: {
      required,
    },
  },
  methods: {
    ...mapActions({
      editTalentSalary: "talentProfile/editTalentSalary",
      editTalentAvailability: "talentProfile/editTalentAvailability",
      updateTalentProfile: "talentProfile/updateTalentProfile",
    }),
    initSalaryData(profile = this.profile) {
      this.partTimeSalary = profile?.basicInfo?.part_time_salary || "";
      this.fullTimeSalary = profile?.basicInfo?.salary || "";
      this.partTimeAvailability = !isNaN(profile.basicInfo?.part_time_status)
        ? profile.basicInfo?.part_time_status
        : null;
      this.fullTimeAvailability = !isNaN(profile.basicInfo?.full_time_status)
        ? profile.basicInfo?.full_time_status
        : null;

      // Store original values
      this.originalValues = {
        fullTimeAvailability: this.fullTimeAvailability,
        partTimeAvailability: this.partTimeAvailability,
        fullTimeSalary: this.fullTimeSalary,
        partTimeSalary: this.partTimeSalary,
      };
    },
    startEditing() {
      this.isEditing = true;
    },
    async handleSaveAll() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        await this.updateTalentProfile({
          talentId: this.profileId,
          full_time_status: this.fullTimeAvailability,
          part_time_status: this.partTimeAvailability,
          full_time_salary: this.fullTimeSalary,
          part_time_salary: this.partTimeSalary,
        });

        this.isEditing = false;
        this.initSalaryData();
      } catch (error) {
        console.error("Error saving changes:", error);
      }
    },
    cancelAllChanges() {
      this.$v.$reset();
      this.isEditing = false;
      this.initSalaryData();
    },
  },
};
</script>
